import { useQuery } from "@tanstack/react-query";
import { useRollbar } from "@rollbar/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import Illustration from "../../assets/images/illustration-unsubscribe.svg";
import { fetchInvitationQuery, unsubscribeReminder } from "../../behavior/api/invitation";
import { isLoggedIn } from "../../behavior/security/auth.service";
import { Heading } from "../../components/elements";
import Header from "../../components/header/Header";
import type { initialValues } from "../../components/unsubscribeFromEmails/ReasonsWrapper";
import ReasonsWrapper from "../../components/unsubscribeFromEmails/ReasonsWrapper";
import { REASONS } from "../../components/unsubscribeFromEmails/constants";
import TransitionPage from "../../pages/transition/TransitionPage";
import type { Invitation } from "../../types/invitation";
import "./UnsubscribeFromEmails.css";

const UnsubscribeFromEmails = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const { t } = useTranslation();
    const rollbar = useRollbar();
    const [submittedUnsubscribe, setSubmittedUnsubscribe] = useState<boolean>(false);
    const { data: invitation, isLoading: loading } = useQuery<Invitation>({
        queryKey: ["invitation-unsubscribe", token],
        queryFn: fetchInvitationQuery(token),
        enabled: !!token,
    });

    const isUnsubscribed = submittedUnsubscribe || (invitation?.unsubscribed ?? false);
    const jobTitle = invitation?.job?.title ?? "<JobTitle>";

    useEffect(() => {
        if (!loading && invitation) {
            const type = invitation.__typename ?? invitation.type;
            if (type === "InvitationInvalid" || type === "InvitationExpired") {
                toast.warning("Could not get job title: " + (invitation.message ?? ""));
            }
        }
    }, [invitation, loading]);

    const handleOnSubmit = async ({ reason, yourReason }: typeof initialValues) => {
        if (!token) return;

        try {
            const response = await unsubscribeReminder({
                token,
                reason,
                otherReason: reason === REASONS.OTHER ? yourReason : undefined,
            });

            if (response.success) {
                setSubmittedUnsubscribe(true);
            } else {
                toast.error(t("UnsubscribeFromEmails.Error_ServerIssue"));
            }
        } catch (error) {
            rollbar.error("Error unsubscribing application", [error], { token });
            toast.error(t("UnsubscribeFromEmails.Error_ServerIssue"));
        }
    };

    return (
        <>
            {isLoggedIn() ? <Header type="LoggedIn" /> : <Header />}
            <div className="unsubscribe-wrapper">
                {!loading ? (
                    <>
                        <Heading level="h3" light>
                            {!isUnsubscribed ? t("UnsubscribeFromEmails.Title") : jobTitle}
                        </Heading>
                        <img src={Illustration} className="illustration" alt="" />
                        <Heading level="h5">
                            {!isUnsubscribed
                                ? `${t("UnsubscribeFromEmails.Subtitle")} ${jobTitle}`
                                : t("UnsubscribeFromEmails.Success_Title")}
                        </Heading>
                        {!isUnsubscribed && <ReasonsWrapper handleOnSubmit={handleOnSubmit} />}
                    </>
                ) : (
                    <TransitionPage title="My Hubert" subtitle={t("UnsubscribeFromEmails.Loading")} />
                )}
            </div>
        </>
    );
};

export default UnsubscribeFromEmails;
